import React, { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import Swal from "sweetalert2";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { useTable, usePagination, useSortBy, useGlobalFilter } from "react-table";
import { useNavigate } from "react-router-dom";

/* ---------------- Global Search ---------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
    placeholder="Search items..."
    className="form-control"
    style={{ marginBottom: "10px" }}
  />
);

const MMSMapping = ({ permissions }) => {
  const [mappedItems, setMappedItems] = useState([]);
  const [typeFilter, setTypeFilter] = useState("All");

  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const navigate = useNavigate();

  /* ---------------- Fetch Mapped Items ---------------- */
  const fetchApprovedItems = async () => {
    try {
      const res = await apiClient.get(`${SERVER_BASE}/get-items-approved`, {
        params: { centerCode: code },
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = res.data || [];

      // ✅ Only mapped items
      const mapped = items.filter(
        (item) =>
          (item.mmsCode && item.mmsCode.trim() !== "") ||
          (item.mmsName && item.mmsName.trim() !== "")
      );

      setMappedItems(mapped);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch items", "error");
      setMappedItems([]);
    }
  };

  useEffect(() => {
    fetchApprovedItems();
  }, []);

  /* ---------------- Edit ---------------- */
  const handleEdit = (item) => {
    navigate("../MMSMappingForm", { state: { item } });
  };

  /* ---------------- Columns ---------------- */
  const columns = React.useMemo(() => {
    const cols = [
      {
        Header: "S.No",
        Cell: ({ row }) => row.index + 1,
      },
      { Header: "Inventory", accessor: "printName" },
      { Header: "MMS Name", accessor: "mmsName" },
      { Header: "MMS Code", accessor: "mmsCode" },
      { Header: "HSN", accessor: "hsn" },
      { Header: "UOM", accessor: "uom" },
      { Header: "MMS UOM", accessor: "secondaryUom" },
      // { Header: "Depts", accessor: "concernedDepartments" },
      {
        Header: "Mapped By",
        accessor: "mappedByName",
        
      },
    ];

    if (role === "itManager") {
      cols.push({
        Header: "Actions",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-outline-dark"
            onClick={() => handleEdit(row.original)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        ),
      });
    }

    return cols;
  }, [permissions, role]);

  /* ---------------- Filters ---------------- */
  const filteredItems = React.useMemo(() => {
    if (typeFilter === "All") return mappedItems;
    return mappedItems.filter((i) => i.type === typeFilter);
  }, [mappedItems, typeFilter]);

  /* ---------------- Table ---------------- */
  const storedPageIndex = parseInt(localStorage.getItem("mmsMappedPageIndex")) || 0;
  const storedPageSize = parseInt(localStorage.getItem("mmsMappedPageSize")) || 25;

  const tableInstance = useTable(
    {
      columns,
      data: filteredItems,
      initialState: {
        pageIndex: storedPageIndex,
        pageSize: storedPageSize,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    setPageSize,
  } = tableInstance;

  useEffect(() => {
    localStorage.setItem("mmsMappedPageIndex", state.pageIndex);
    localStorage.setItem("mmsMappedPageSize", state.pageSize);
  }, [state.pageIndex, state.pageSize]);

  /* ---------------- Render ---------------- */
  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Inventory Master - MMS Mapped</h3>
        <div>
          Mapped Items: <strong>{mappedItems.length}</strong>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-2">
        <div className="col-md-9">
          <GlobalFilter
            filter={state.globalFilter}
            setFilter={setGlobalFilter}
          />
        </div>

        {/* <div className="col-md-3">
          <select
            className="form-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Asset">AC - Asset</option>
            <option value="Consumables">CU - Consumables</option>
            <option value="Fixtures & Fittings">
              FF - Fixtures & Fittings
            </option>
          </select>
        </div> */}

        <div className="col-md-3">
          <input
            readOnly
            className="form-control"
            value={`${page.length} Count Per Page`}
          />
        </div>
      </div>

      {/* Table */}
      <table
        {...getTableProps()}
        className="table table-striped"
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <button
            className="btn btn-secondary me-2"
            onClick={previousPage}
            disabled={!canPreviousPage}
          >
            Previous
          </button>
          <button
            className="btn btn-secondary"
            onClick={nextPage}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>

        <span>
          Page {state.pageIndex + 1} of {pageOptions.length}
        </span>

        <select
          className="form-select w-auto"
          value={state.pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[25, 50, 75, 100].map((s) => (
            <option key={s} value={s}>
              Show {s}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default MMSMapping;
